import { Link } from "react-router-dom";
import { site } from "../data/site";

/**
 * Hero — official SRM hero background + headline + copy + CTAs.
 *
 *   • Background: official strata apartment photograph.
 *   • Dark navy gradient overlay from the left for type contrast.
 *   • Large in-hero logo has been removed — only the small logo in the
 *     site header is shown.
 *   • Headline, supporting paragraph and buttons are unchanged.
 */
export default function Hero() {
    const { hero } = site;

    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative isolate w-full overflow-hidden bg-srm-navyDark"
        >
            {/* Background image — responsive WebP with PNG fallback.
                The original 2.1 MB PNG was the dominant LCP element; the
                WebP variants below cut total hero bytes by ~15× and lift
                LCP on mobile from ~20 s to well under 2 s. */}
            <div className="absolute inset-0 -z-10">
                <picture>
                    <source
                        type="image/webp"
                        srcSet="/assets/hero-background-640.webp 640w, /assets/hero-background-1024.webp 1024w, /assets/hero-background.webp 1536w"
                        sizes="100vw"
                    />
                    <img
                        src="/assets/hero-background.jpg"
                        alt="Modern Australian strata apartment building"
                        width="1536"
                        height="1024"
                        fetchPriority="high"
                        loading="eager"
                        decoding="async"
                        className="h-full w-full object-cover object-[75%_center]"
                        style={{ transform: "scale(0.95)" }}
                    />
                </picture>
                {/* Dark navy gradient from the left — deepened on the type-side for executive contrast. */}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(90deg, rgba(15,40,86,0.94) 0%, rgba(15,40,86,0.82) 28%, rgba(15,40,86,0.42) 48%, rgba(15,40,86,0.08) 70%, rgba(15,40,86,0) 92%)",
                    }}
                />
                {/* Subtle vertical lift for readability behind the headline */}
                <div
                    aria-hidden
                    className="absolute inset-y-0 left-0 w-2/3"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(15,40,86,0) 0%, rgba(15,40,86,0.20) 60%, rgba(15,40,86,0.35) 100%)",
                    }}
                />
                {/* Soft bottom darken for trust-strip transition */}
                <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-srm-navyDark/60" />
            </div>

            <div className="container-srm pt-20 pb-24 lg:pt-44 lg:pb-48 min-h-[620px] lg:min-h-[920px] flex items-center">
                <div className="max-w-3xl lg:max-w-[44rem]">
                    {hero.tagline && (
                        <div
                            data-testid="hero-tagline"
                            className="font-display font-bold tracking-[0.26em] uppercase text-srm-gold text-[12px] lg:text-[13px]"
                        >
                            {hero.tagline}
                        </div>
                    )}
                    <h1
                        data-testid="hero-headline"
                        className="mt-6 lg:mt-8 font-display font-bold text-[2.5rem] sm:text-[3.25rem] lg:text-[4rem] xl:text-[4.5rem] tracking-[-0.028em] leading-[1.05] text-white text-balance"
                        style={{ textShadow: "0 2px 24px rgba(15,40,86,0.35)" }}
                    >
                        {hero.headlineLines ? (
                            hero.headlineLines.map((line) => (
                                <span key={line} className="block lg:whitespace-nowrap">
                                    {line}
                                </span>
                            ))
                        ) : (
                            hero.headline
                        )}
                    </h1>

                    {hero.sub && (
                        <p
                            data-testid="hero-sub"
                            className="mt-7 lg:mt-10 text-[17.5px] sm:text-[19.5px] lg:text-[21px] text-white/85 leading-[1.6] lg:leading-[1.75] max-w-[550px]"
                        >
                            {hero.sub}
                        </p>
                    )}

                    <div className="mt-9 lg:mt-14 flex flex-col sm:flex-row gap-4">
                        <Link
                            to={hero.primaryCta.href}
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[12px] font-semibold tracking-[0.2em] uppercase px-10 h-[56px] border border-srm-gold shadow-[0_10px_30px_rgba(201,165,92,0.30)] hover:bg-srm-goldDark hover:border-srm-goldDark hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(201,165,92,0.40)] transition-all duration-300"
                        >
                            {hero.primaryCta.label}
                        </Link>
                        {hero.secondaryCta && (
                            <Link
                                to={hero.secondaryCta.href}
                                data-testid="hero-cta-secondary"
                                className="group inline-flex items-center justify-center gap-2 rounded-sm bg-transparent text-white text-[12px] font-semibold tracking-[0.2em] uppercase px-10 h-[56px] border border-white/40 hover:bg-white/10 hover:border-white hover:-translate-y-0.5 transition-all duration-300"
                            >
                                {hero.secondaryCta.label}
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
